import React, { useEffect, useMemo, useState } from 'react'
import { Redirect } from 'react-router-dom'
import auth from '../auth/service'
import Callback from './Callback/Callback'

const LOGIN_RECOVERY_ERRORS = new Set([
  'consent_required',
  'interaction_required',
  'login_required',
])

const PASSWORD_RECOVERY_ERRORS = new Set([
  'password_expired',
  'password_expire_first_login',
])

const expiredMessageStyle = {
  display: 'inline-block',
  fontWeight: 900,
  fontSize: '18px',
  color: '#172b4d',
}

const buildReferrer = (location = {}) => {
  const pathname = location.pathname || '/'
  const search = location.search || ''
  return `${pathname}${search}` || '/'
}

const getErrorPayload = (error) => (error && error.response && error.response.data) || null

const getErrorRedirect = (error) => (getErrorPayload(error) && getErrorPayload(error).redirect_to) || null

const getErrorCode = (error) => {
  const payload = getErrorPayload(error)

  return String((payload && payload.code) || (error && (error.error || error.code)) || '').toLowerCase()
}

const getErrorDetails = (error) =>
  (getErrorPayload(error) && getErrorPayload(error).detail) ||
  (error && (error.errorDescription || error.description || error.message)) ||
  null

const getErrorMessage = (error) => {
  const errorCode = getErrorCode(error)

  if (errorCode === 'credential_expired') {
    return (
      <>
        <strong style={expiredMessageStyle}>Le credenziali del tuo account sono scadute.</strong> Contatta
        innovation@webranking.it per riattivarle.
      </>
    )
  }

  if (errorCode === 'password_expired') {
    return "La password del tuo account e' scaduta. Aggiornala prima di continuare."
  }

  if (errorCode === 'password_expire_first_login') {
    return "Per completare il primo accesso devi prima aggiornare la password."
  }

  return 'Non siamo riusciti a verificare il tuo accesso. Riprova la verifica oppure avvia di nuovo il login.'
}

export const AuthGate = ({ children, history, location }) => {
  const referrer = useMemo(() => buildReferrer(location), [location])
  const [authState, setAuthState] = useState({
    authContext: null,
    error: null,
    retryCount: 0,
    status: 'loading',
  })

  useEffect(() => {
    let isMounted = true

    auth
      .bootstrapAppSession()
      .then((authContext) => {
        if (!isMounted) {
          return
        }

        setAuthState((currentState) => ({
          ...currentState,
          authContext,
          error: null,
          status: 'authenticated',
        }))
      })
      .catch((error) => {
        if (!isMounted) {
          return
        }

        const errorCode = getErrorCode(error)

        if (PASSWORD_RECOVERY_ERRORS.has(errorCode) && getErrorRedirect(error)) {
          auth.followRecoveryRedirect(getErrorRedirect(error))
          return
        }

        setAuthState((currentState) => ({
          ...currentState,
          authContext: null,
          error,
          status: LOGIN_RECOVERY_ERRORS.has(errorCode) ? 'unauthenticated' : 'error',
        }))
      })

    return () => {
      isMounted = false
    }
  }, [authState.retryCount])

  if (authState.status === 'loading') {
    return (
      <Callback
        title="Verifica accesso"
        message="Stiamo verificando la sessione e caricando i permessi del tuo account."
      />
    )
  }

  if (authState.status === 'unauthenticated') {
    return (
      <Redirect
        to={{
          pathname: '/login',
          state: {
            referrer,
          },
        }}
      />
    )
  }

  if (authState.status === 'error') {
    return (
      <Callback
        isLoading={false}
        title="Verifica accesso non riuscita"
        message={getErrorMessage(authState.error)}
        details={getErrorDetails(authState.error)}
        actions={
          <>
            <button
              type="button"
              className="btn btn-primary mb-2 mr-sm-2"
              onClick={() =>
                setAuthState((currentState) => ({
                  ...currentState,
                  error: null,
                  status: 'loading',
                  retryCount: currentState.retryCount + 1,
                }))
              }
            >
              Riprova verifica
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary mb-2"
              onClick={() => auth.restartLogin(referrer)}
            >
              Vai al login
            </button>
          </>
        }
      />
    )
  }

  if (React.isValidElement(children)) {
    return React.cloneElement(children, {
      authContext: authState.authContext,
    })
  }

  return children || null
}
